// 📌 Header - Barra superior
import { marca } from '@brand';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Search, Moon, Sun, Command, Bot, LogOut, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui';
import { useThemeStore } from '@/stores/theme.store';
import { useAssistantStore } from '@/stores/assistant.store';
import { useAuth } from '@/lib/hooks';
import { useTabsStore } from '@/stores/tabs.store';
import { usePaletaStore } from '@/stores/paleta.store';
import { LanguageSelector } from '@/components/settings/LanguageSelector';
import { ThemeSelector } from '@/components/settings/ThemeSelector';
import { useTranslation } from '@/lib/hooks/useTranslation';
import { NotificacionesBell } from '@/features/medical/NotificacionesBell';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { theme, toggleDarkMode } = useThemeStore();
  const { enabled: assistantEnabled, toggle: toggleAssistant } = useAssistantStore();
  const { user, logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const openTab = useTabsStore((s) => s.openTab);
  const abrirPaleta = usePaletaStore((s) => s.abrir);
  const { t } = useTranslation();
  const isDark = theme.category === 'dark';

  // Menú de usuario (cerrar sesión)
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [userMenuOpen]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout(); // limpia el token y la cache de react-query
    navigate('/login', { replace: true }); // redirección explícita a la pantalla de login
  };
  const userInitial = (user?.username?.[0] || 'U').toUpperCase();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 px-4">
      {/* Contraer/expandir el sidebar */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onMenuClick}
        title={t('shell.toggle_menu')}
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Buscador global: abre la paleta de comandos (el mismo atajo Ctrl/⌘+K) */}
      <button
        type="button"
        onClick={abrirPaleta}
        title={t('shell.search_menu')}
        className="flex flex-1 items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent max-w-md"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="hidden sm:inline">{t('common.search')}...</span>
        <kbd className="ml-auto hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
          <Command className="h-3 w-3" />K
        </kbd>
      </button>

      {/* Contexto global de la marca (TrackConstruction: obra actual) */}
      {marca.SelectorContexto && <marca.SelectorContexto />}

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Language Selector */}
        <LanguageSelector />

        {/* Theme Selector */}
        <ThemeSelector />

        {/* Theme toggle (quick switch) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleDarkMode}
          title={isDark ? t('settings.lightMode') : t('settings.darkMode')}
        >
          {isDark ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
        </Button>

        {/* Asistente virtual (habilitar/deshabilitar) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleAssistant}
          aria-pressed={assistantEnabled}
          title={assistantEnabled
            ? t('settings.assistantDisable', 'Desactivar asistente virtual')
            : t('settings.assistantEnable', 'Activar asistente virtual')}
        >
          <Bot className={assistantEnabled ? 'h-5 w-5 text-primary' : 'h-5 w-5 text-muted-foreground'} />
        </Button>

        {/* Notifications */}
        <NotificacionesBell />

        {/* User menu (perfil + cerrar sesión) */}
        <div className="relative" ref={userMenuRef}>
          <Button
            variant="ghost"
            className="relative h-9 w-9 rounded-full"
            onClick={() => setUserMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            title={user?.username || t('common.user', 'Usuario')}
          >
            <div className="flex h-full w-full items-center justify-center rounded-full bg-primary text-primary-foreground">
              <span className="text-sm font-semibold">{userInitial}</span>
            </div>
          </Button>

          {userMenuOpen && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-56 rounded-lg border bg-card p-1 shadow-lg z-50"
            >
              <div className="border-b px-3 py-2">
                <p className="truncate text-sm font-medium text-foreground">{user?.username}</p>
                {user?.email && (
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                )}
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setUserMenuOpen(false);
                  openTab({ id: '/mi-cuenta', title: t('cuenta.titulo'), path: '/mi-cuenta' });
                }}
                className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent"
              >
                <KeyRound className="h-4 w-4" />
                {t('cuenta.miCuenta')}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-destructive transition-colors hover:bg-accent disabled:opacity-50"
              >
                <LogOut className="h-4 w-4" />
                {t('auth.logout', 'Cerrar Sesión')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
